import React, { useEffect, useState } from "react";
import { Rat } from "lucide-react";

// Petak sawah isometrik 2×3 (SVG murni, proyeksi 2:1 seperti peta kota) — dipakai Scene (diam, semua kosong) dan mini game Sawah.
// Terbenam di tanah: lahan rumput → pematang tanah → petak cekung (dinding belakang gelap, dasar air turun 4 px) — bukan kotak melayang.
// petak[i] = { fase: kosong|benih|basah|siap|layu, hama: bool, progres: 0..1 (pertumbuhan saat basah), peringatan: bool (hampir layu) }
export const KOLOM = 3, BARIS = 2, W = 110, H = 55;
export const KOSONG6 = Array.from({ length: KOLOM * BARIS }, () => ({ fase: "kosong" }));
const iso = (c, r) => [(c - r) * (W / 2), (c + r) * (H / 2)];
const posisi = i => iso(i % KOLOM, Math.floor(i / KOLOM));
const diamond = (hw, hh) => `0,${-hh} ${hw},0 0,${hh} ${-hw},0`;
const KEDALAMAN = 4, INSET = 7;
const AIR = { kosong: "#86b1c4", benih: "#86b1c4", basah: "#6a9db5", siap: "#7fadc0", layu: "#9fae98" };
// Lahan rumput di sekeliling petak (koordinat tile kontinu)
const LAHAN = [iso(-0.75, -0.75), iso(KOLOM - 0.25, -0.75), iso(KOLOM - 0.25, BARIS - 0.25), iso(-0.75, BARIS - 0.25)].map(p => p.join(",")).join(" ");

function Padi({ warna, tinggi, bengkok = 0, bulir }) {
  const titik = [[-24, 4], [-8, -6], [10, 6], [24, -3], [0, 12], [-14, 14], [16, 15]];
  return titik.map(([x, y], i) => (
    <g key={i} transform={`translate(${x} ${y})`}>
      <path d={`M0 0 q${-4 + bengkok} ${-tinggi * .5} ${-3 + bengkok * 2} ${-tinggi} M0 0 q${1 + bengkok} ${-tinggi * .55} ${2 + bengkok * 2} ${-tinggi * 1.05} M0 0 q${5 + bengkok} ${-tinggi * .5} ${7 + bengkok * 2} ${-tinggi * .9}`} stroke={warna} strokeWidth="2.2" fill="none" strokeLinecap="round" />
      {bulir && <circle cx={2 + bengkok * 2} cy={-tinggi * 1.05} r="2.6" fill="#e2b53c" />}
    </g>
  ));
}

function Isi({ p }) {
  switch (p.fase) {
    case "benih": return <g fill="#6b4423">{[[-10, -4], [6, -8], [14, 4], [-4, 8], [-18, 6], [2, 0]].map(([x, y], i) => <ellipse key={i} cx={x} cy={y} rx="3" ry="2" />)}</g>;
    case "basah": return <Padi warna="#2e9e5b" tinggi={8 + (p.progres || 0) * 22} />;
    case "siap": return <g style={p.peringatan ? { animation: "jw-blink .5s steps(2) infinite" } : undefined}><Padi warna="#c9a227" tinggi={32} bulir /></g>;
    case "layu": return <Padi warna="#8a6a3c" tinggi={16} bengkok={6} />;
    default: return <g opacity=".7"><circle r="14" fill="none" stroke="#fff" strokeWidth="1.5" strokeDasharray="4 4" /><path d="M0 7 V-2 M0 -2 q-7 -1 -8 -8 q8 0 8 8 M0 -2 q7 -1 8 -8 q-8 0 -8 8" stroke="#fff" strokeWidth="1.7" fill="none" strokeLinecap="round" /></g>;
  }
}

// Satu petak cekung: pematang (diamond penuh, tanah) → dinding belakang (NW & NE) → dasar air turun KEDALAMAN
function Petak({ p }) {
  const hw = W / 2 - INSET, hh = H / 2 - INSET / 2, d = KEDALAMAN;
  return (
    <>
      <polygon points={diamond(W / 2, H / 2)} fill="#a3784c" />
      <polygon points={diamond(W / 2, H / 2)} fill="none" stroke="#8d6640" strokeWidth="1" opacity=".6" />
      <polygon points={`0,${-hh} ${-hw},0 ${-hw},${d} 0,${-hh + d}`} fill="#5e4027" />
      <polygon points={`0,${-hh} ${hw},0 ${hw},${d} 0,${-hh + d}`} fill="#704d2e" />
      <g transform={`translate(0 ${d})`}>
        <polygon points={diamond(hw, hh)} fill={AIR[p.fase] || AIR.kosong} />
        <ellipse cx="-10" cy="-6" rx="22" ry="6" fill="#fff" opacity=".16" />
        <Isi p={p} />
      </g>
    </>
  );
}

// Petani kecil: berjalan (transisi transform) ke pematang depan petak yang diketuk, lalu beraksi — menunduk (tanam/panen/bersih),
// menyiram (miring + tetes air), melompat (usir hama). Tanpa target → berdiri santai di depan sawah.
const IDLE = iso(1, 2.15);
const DEPAN = i => { const [x, y] = posisi(i); return [x, y + H / 2 + 4]; };
const KF_PETANI = `@keyframes jw-jalan{0%,100%{transform:translateY(0)}50%{transform:translateY(-2.5px)}}
@keyframes jw-kaki{0%,100%{transform:rotate(-18deg)}50%{transform:rotate(18deg)}}
@keyframes jw-nunduk{0%,100%{transform:rotate(0)}45%,65%{transform:rotate(38deg)}}
@keyframes jw-siram{0%,100%{transform:rotate(0)}30%,80%{transform:rotate(-14deg)}}
@keyframes jw-lompat{0%,100%{transform:translateY(0)}40%{transform:translateY(-12px)}}
@keyframes jw-tetes{0%{opacity:0;transform:translateY(0)}20%{opacity:1}100%{opacity:0;transform:translateY(16px)}}`;
const AKSI_ANIM = { tanam: "jw-nunduk .5s ease-in-out", panen: "jw-nunduk .5s ease-in-out", bersih: "jw-nunduk .5s ease-in-out", siram: "jw-siram .6s ease-in-out", usir: "jw-lompat .45s ease-out" };
const JALAN_MS = 380;

function Petani({ perintah }) {
  const [state, setState] = useState({ pos: IDLE, flip: false, jalan: false, aksi: null });
  useEffect(() => {
    if (!perintah) return;
    const tujuan = DEPAN(perintah.i);
    setState(s => ({ pos: tujuan, flip: tujuan[0] < s.pos[0], jalan: Math.hypot(tujuan[0] - s.pos[0], tujuan[1] - s.pos[1]) > 2, aksi: null }));
    const t1 = setTimeout(() => setState(s => ({ ...s, jalan: false, aksi: perintah.aksi })), JALAN_MS);
    const t2 = setTimeout(() => setState(s => ({ ...s, aksi: null })), JALAN_MS + 650);
    return () => { clearTimeout(t1); clearTimeout(t2); };
  }, [perintah]);
  const { pos, flip, jalan, aksi } = state;
  const kaki = jalan ? { animation: "jw-kaki .22s ease-in-out infinite", transformBox: "fill-box", transformOrigin: "50% 0%" } : undefined;
  return (
    <g data-testid="petani-kecil" data-aksi={aksi || (jalan ? "jalan" : "diam")} style={{ transform: `translate(${pos[0]}px, ${pos[1]}px)`, transition: `transform ${JALAN_MS}ms ease-in-out` }}>
      <ellipse rx="11" ry="4" fill="rgba(0,0,0,.18)" />
      <g style={{ transform: `scaleX(${flip ? -1 : 1})`, transformBox: "fill-box", transformOrigin: "center" }}>
        <g style={{ animation: jalan ? "jw-jalan .22s ease-in-out infinite" : AKSI_ANIM[aksi] || "none", transformBox: "fill-box", transformOrigin: "50% 100%" }}>
          <rect x="-6.5" y="-16" width="5.5" height="16" rx="2" fill="#2b3a55" style={kaki} />
          <rect x="1" y="-16" width="5.5" height="16" rx="2" fill="#2b3a55" style={kaki ? { ...kaki, animationDelay: ".11s" } : undefined} />
          <rect x="-9" y="-37" width="18" height="23" rx="5" fill="#2e58c4" />
          <rect x="-13.5" y="-35" width="4.5" height="16" rx="2" fill="#2e58c4" />
          <rect x="9" y="-35" width="4.5" height="16" rx="2" fill="#2e58c4" />
          <circle cx="-11.3" cy="-18" r="2.6" fill="#f1c9a5" /><circle cx="11.3" cy="-18" r="2.6" fill="#f1c9a5" />
          <circle cy="-43" r="7" fill="#f1c9a5" />
          <path d="M-16 -45 L0 -60 L16 -45 Z" fill="#d9a857" /><ellipse cy="-45" rx="16" ry="3.5" fill="#c9954a" />
          {aksi === "siram" && <g>
            <rect x="12" y="-30" width="11" height="8" rx="2" fill="#5b7fd6" /><rect x="22" y="-29" width="7" height="2.5" fill="#5b7fd6" />
            {[0, 1, 2].map(k => <circle key={k} cx={30 + k * 3} cy="-24" r="1.6" fill="#8fc7ff" style={{ animation: `jw-tetes .5s ${k * .12}s ease-in infinite` }} />)}
          </g>}
        </g>
      </g>
    </g>
  );
}

export function PetakSawah({ petak = KOSONG6, onTap, pops = [], perintah, style }) {
  return (
    <div data-testid="petak-sawah" style={{ position: "absolute", left: "50%", top: "50%", transform: "translate(-50%,-50%)", width: "min(96vw, 460px)", pointerEvents: onTap ? "auto" : "none", ...style }}>
      <style>{`@keyframes jw-blink{0%,100%{opacity:1}50%{opacity:.45}}@keyframes jw-hama{0%,100%{transform:translateY(0)}50%{transform:translateY(-4px)}}
        @keyframes jw-popsvg{0%{opacity:1;transform:translateY(0)}100%{opacity:0;transform:translateY(-34px)}}${KF_PETANI}`}</style>
      <svg viewBox="-165 -95 390 250" width="100%" style={{ display: "block", overflow: "visible" }}>
        {/* lahan rumput + pematang keliling */}
        <polygon points={LAHAN} fill="#9bd27a" stroke="#a3784c" strokeWidth="7" strokeLinejoin="round" />
        <polygon points={LAHAN} fill="none" stroke="#86bd66" strokeWidth="1.5" />
        {petak.map((p, i) => {
          const [x, y] = posisi(i);
          return (
            <g key={i} data-testid={`petak-${i}`} data-fase={p.fase} data-hama={p.hama ? "1" : "0"} transform={`translate(${x} ${y})`} onPointerDown={onTap ? e => { e.preventDefault(); onTap(i); } : undefined} style={{ cursor: onTap ? "pointer" : "default" }}>
              <Petak p={p} />
              {p.hama && (
                <g data-testid={`hama-${i}`} style={{ animation: "jw-hama .6s ease-in-out infinite", transformBox: "fill-box", transformOrigin: "center" }}>
                  <ellipse cy="2" rx="12" ry="5" fill="rgba(0,0,0,.18)" />
                  <circle cy="-22" r="16" fill="#fff" stroke="#dc2626" strokeWidth="2.5" />
                  <Rat x={-10} y={-32} size={20} color="#8a5a3c" />
                </g>
              )}
            </g>
          );
        })}
        {pops.map(pp => { const [x, y] = posisi(pp.i); return <text key={pp.k} x={x} y={y - 22} textAnchor="middle" fontSize="15" fontWeight="800" fill={pp.warna || "#fff"} stroke="rgba(0,0,0,.35)" strokeWidth=".6" paintOrder="stroke" style={{ animation: "jw-popsvg .8s ease-out forwards", transformBox: "fill-box", transformOrigin: "center", fontFamily: "inherit", pointerEvents: "none" }}>{pp.teks}</text>; })}
        <Petani perintah={perintah} />
      </svg>
    </div>
  );
}
